<template>
    <div class="kt-modal-wrap">
        <Transition name="modal-fade">
            <div 
                :id="props.id" 
                :class="['modal modal-wrap', 'd-block']" 
                tabindex="-1" 
                ref="divModal" 
                v-if="isVisible"
            >
                <div class="modal-dialog" :class="props.size">
                    <div class="modal-content">
                    <div class="modal-header" v-if="props.title || props.dismissable || slots.header">
                        <div 
                            v-if="props.title"
                            class="modal-title" 
                            v-html="props.title"
                        ></div>
                        <button 
                            v-if="props.dismissable"
                            type="button" 
                            class="btn-close" 
                            aria-label="Close"
                            @click="dismiss()"
                        ></button>
                        <slot name="header"></slot>
                    </div>
                    <div class="modal-body" v-if="props.body || slots.body">
                        <div b-html="props.body"></div>
                        <slot name="body"></slot>
                    </div>
                    <div 
                        v-if="slots.footer || props.buttons"
                        class="modal-footer kt-modal-footer">
                        <button 
                            v-for="btn in props.buttons"
                            type="button" 
                            :class="['btn', btn.css]"
                            @click="$emit('click', btn)"
                        >
                            <span v-html="btn.html"></span>
                            <span>{{ btn.text }}</span>
                        </button>
                        <slot name="footer"></slot>
                    </div>
                    </div>
                </div>
            </div>
        </Transition>
    </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted, Transition } from "vue";
// import * as bootstrap from 'bootstrap';

interface ModalButton {
    css?: string;
    html?: string;
    text?: string;
}

const slots = defineSlots<{
    header?: () => any;
    body?: () => any;
    footer?: () => any;
}>();
const divModal = ref<HTMLElement | null>(null);
const modal = ref(null);
const isVisible = ref(false);

const emit = defineEmits<{
    (e: "dismiss"): void;
    (e: "click", button: ModalButton): void;
}>();

const props = withDefaults(defineProps<{
    id?: string;
    visible?: boolean;
    title?: string;
    size?: string;
    dismissable?: boolean;
    body?: string;
    buttons?: ModalButton[];
    options?: { backdrop?: string; keyboard?: boolean; focus?: boolean };
}>(), {
    id: () => "kt-modal-" + (Math.random() + "").replace(".", "") + "-" + (+ new Date()),
    visible: false,
    title: "",
    size: "",
    dismissable: true,
    body: "",
    buttons: () => [],
    options: () => ({ backdrop: "static", keyboard: true, focus: true }),
});

const dismiss = () =>
{
    emit("dismiss");
    toggleModal(false);
};

const toggleModal = (show?: boolean) =>
{
    if (typeof show !== "undefined")
    {   
        isVisible.value = !!show;
    }

    else
    {
        isVisible.value = !isVisible.value;
    }
}

const unwatch = watch(() => props.visible, toggleModal /*, { immediate: true } */);

onMounted(() =>
{    
    toggleModal(props.visible);
});

onUnmounted(() =>
{
    unwatch();
});

</script>

<style>

.kt-modal-wrap > .modal-wrap {
    background: rgba(0, 0, 0, 0.5);
}

 /* Modal fade and slide animations */
 .modal-fade-enter-active {
     transition: opacity 0.25s ease-out;
 }
 
 .modal-fade-leave-active {
     transition: opacity 0.18s ease-in;
 }
 
 .modal-fade-enter-from {
     opacity: 0;
 }

 .modal-fade-enter-from .modal-dialog {
     transform: translateY(-30px);
 }

 .modal-fade-leave-from {
     opacity: 1;
 }

 .modal-fade-leave-from .modal-dialog {
     transform: translateY(0);
 }
 
 .modal-fade-leave-to {
     opacity: 0;
 }
 
 .modal-fade-leave-to .modal-dialog {
     transform: translateY(-10px);
 }
 
 .modal-dialog {
     transition: transform 0.25s ease-out;
 }

 .modal-fade-leave-active .modal-dialog {
     transition: transform 0.18s ease-in;
 }

/* Make modal-xl nearly full-width with gutters */
.kt-modal-wrap .modal-dialog.modal-xl {
    max-width: calc(100% - 1rem);
    margin: 0.5rem;
}

@media (min-width: 576px) {
    .kt-modal-wrap .modal-dialog.modal-xl {
        max-width: calc(100% - 2rem);
        margin: 1rem;
    }
}

/* Modal footer button styling */
.kt-modal-footer {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
}

/* Mobile: buttons stack and span full width */
@media (max-width: 575.98px) {
    .kt-modal-footer {
        flex-direction: column;
    }
    
    .kt-modal-footer > .btn,
    .kt-modal-footer > button,
    .kt-modal-footer .kt-modal-actions > .btn,
    .kt-modal-footer .kt-modal-actions > button {
        width: 100%;
    }
    
    .kt-modal-footer .kt-modal-actions {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }
}

/* Desktop: buttons right-aligned */
@media (min-width: 576px) {
    .kt-modal-footer {
        justify-content: flex-end;
    }
    
    .kt-modal-footer .kt-modal-actions {
        display: flex;
        gap: 0.5rem;
        justify-content: flex-end;
    }
}

</style>